import ApiRoutes from "../constants/ApiRoutes";
import Constants from "../constants/Constants";
import ApiClient from "../utilities/api-client";
import ErrorUtils from "../utilities/error-utils";
import CartService from "./cart-service";

const AuthenticationService =  {
    /** ---------------------------------------------------------------
     * Method : login
     * Description : API call to check user authentication
     * @param {*} { email, password } 
     * @returns 
     */
    login : async (encryptedData) => {
        let response;
        try{
            const url = `${ApiRoutes.AUTH.ROOT_CONTEXT}${ApiRoutes.AUTH.LOGIN}`;
            response = await ApiClient.post(url, { payload : encryptedData });
            if(response?.data?.isSuccess) {
                AuthenticationService.setUserDetails(response?.data);
            }
            return {
                isSuccess : true,
                userRole : response?.data?.userRole
            }
        } catch(e) {
            throw ErrorUtils.processError(e);
        }
    },

    /** ---------------------------------------------------------------
     * Method : forgotPasswordSendEmail
     * Description : Service call to send password to user email
     */
    forgotPasswordSendEmail : async(data) => {
        let response;
        try{
            const url = `${ApiRoutes.AUTH.ROOT_CONTEXT}${ApiRoutes.AUTH.RESET_PASSWORD}`;
            response = await ApiClient.post(url, data);
            return response?.data?.isSuccess || false;
        } catch(e) {
            throw ErrorUtils.processError(e);
        }
    },

     /** ---------------------------------------------------------------
     * Method : getEventUpdates
     * Description : Service call to get Event updates from server
     */
     getEventUpdates : async() => {
        let response;
        try{
            const url = `${ApiRoutes.EMPLOYEE.ROOT_CONTEXT}${ApiRoutes.EMPLOYEE.GET_EVENT_UPDATES}`;
            response = await ApiClient.get(url);
            if(response?.data?.isSuccess) {
               AuthenticationService.setEventData(response?.data?.event);
            }
        } catch(e) {
            throw ErrorUtils.processError(e);
        }
    },

    /** ---------------------------------------------------------------
     * Method : updatePassword
     * Description : Service call to update user password 
     */
    updatePassword : async(encryptedData) => {
        let response;
        try{
            const url = `${ApiRoutes.AUTH.ROOT_CONTEXT}${ApiRoutes.AUTH.UPDATE_PASSWORD}`;
            response = await ApiClient.post(url, { payload: encryptedData });
            return response?.data?.isSuccess || false;
        } catch(e) {
            throw ErrorUtils.processError(e);
        }
    },

    /** ---------------------------------------------------------------
     * Method : setUserDetails
     * Description : To get user details from LS
     */
    setUserDetails : async (data) => {
        if(data) {
            const user = {
                name : data.name,
                locationDetails : data.locationDetails,
                userRole : data.userRole,
                email : data.email,
            }
            if(user.userRole === Constants.USER_ROLES.VENDOR) {
                user.vendorId = data.vendorId;
            }
            localStorage.setItem(Constants.LOCAL_STORAGE.USER, JSON.stringify(user));
            localStorage.setItem(Constants.LOCAL_STORAGE.ACCESS_TOKEN, data.token);
            if(data.balance && data.balance.event) localStorage.setItem(Constants.LOCAL_STORAGE.EVENT, JSON.stringify(data.balance.event));
            if(data.balance) AuthenticationService.setUserBalance(data.balance);
        }
    },

    /** ---------------------------------------------------------------
     * Method : getUserDetails
     * Description : To get user details from LS
     * @returns userDetails
     */
    getUserDetails : async() => {
        const userDetails = await JSON.parse(localStorage.getItem(Constants.LOCAL_STORAGE.USER));
        return userDetails;
    },
    
    /** ---------------------------------------------------------------
     * Method : getAccessToken
     * Description : To get access token from LS
     * @returns string
     */
    getAccessToken : () => {
        const accessToken = localStorage.getItem(Constants.LOCAL_STORAGE.ACCESS_TOKEN);
        return accessToken;
    },

    /** ---------------------------------------------------------------
     * Method : getUserBalance
     * Description : To get user Balance
     * @returns number
     */
    getUserBalance : () => {
        // *** added ****
        const balance = JSON.parse(localStorage.getItem(Constants.LOCAL_STORAGE.USER_BALANCE));
        return balance;
    },

    /** ---------------------------------------------------------------
     * Method : setUserBalance
     * Description : To set user Balance
     * @returns number
     */
    setUserBalance : (balance) => {
        if(balance?.event && !balance?.event?.amount) {
            balance.event.amount = 0;
        }
        localStorage.setItem(Constants.LOCAL_STORAGE.USER_BALANCE, JSON.stringify(balance));
    },

    /** ---------------------------------------------------------------
     * Method : checkIfValidSessionExists
     * Description : To check whether a valid session and valid user exists in LS
     * @returns isValid (boolean), userDetails (object)
     */
    checkIfValidSession : async() => { 
        const accessToken = await AuthenticationService.getAccessToken();
        let userDetails = null;
        if(accessToken){
            userDetails = await AuthenticationService.getUserDetails();
            if(userDetails) {
                // Ensuring no data is tampered in the local storage
                if(userDetails.hasOwnProperty("userRole") && 
                    userDetails.hasOwnProperty("name") &&
                    userDetails.hasOwnProperty("locationDetails")
                ) {
                    return {
                        isValidSession : true,
                        userDetails,
                        accessToken
                    };
                }
            }
        }
        return {
            isValidSession : false,
            userDetails : null,
            accessToken : null
        };
    },

    /** ---------------------------------------------------------------
     * Method : getPublicKey
     * Description : To retrieve the RSA public key.
     * @returns isSuccess (boolean), publicKeyPem (string)
     */
    getPublicKey : async() => {
        let response;
        try{
            const url = `${ApiRoutes.AUTH.ROOT_CONTEXT}${ApiRoutes.AUTH.PUBLIC_KEY}`;
            response = await ApiClient.get(url);
            return response?.data?.isSuccess 
                ? { isSuccess: true, publicKey: response?.data?.publicKeyPem }
                : { isSuccess: false };
        } catch(e) {
            throw ErrorUtils.processError(e);
        }
    },

    /** ---------------------------------------------------------------
     * Method : logout
     * Description : To logout user, clear LS
     */
    logout : async () => {
        CartService.clearCart();
        localStorage.clear();
    },

    setEventDetails: (selectedLocation) => {
        let event;
        event = AuthenticationService.getEventDetails();
        if(!event) {
                event = {
                    user: {
                        selectedLocation
                    }
                }
        } else {
            event.user[Constants.LOCAL_STORAGE.SELECTED_LOCATION] = selectedLocation
        }
        localStorage.setItem(Constants.LOCAL_STORAGE.USERS_EVENT, JSON.stringify(event));
    },

    getEventDetails: () => {
        let event = JSON.parse(localStorage.getItem(Constants.LOCAL_STORAGE.USERS_EVENT));
        return event;
    },

    getEventData: () => {
        let event = JSON.parse(localStorage.getItem(Constants.LOCAL_STORAGE.EVENT));
        return event;
    },

    setEventData: (event) => {
        localStorage.setItem(Constants.LOCAL_STORAGE.EVENT, JSON.stringify(event));
    },

    setLocationDetails: (locationDetails) => {
        localStorage.setItem(Constants.LOCAL_STORAGE.LOCATION_DETAILS, JSON.stringify(locationDetails));
    },

    getLocationDetails: () => {
        let locationDetails = JSON.parse(localStorage.getItem(Constants.LOCAL_STORAGE.LOCATION_DETAILS));
        return locationDetails;
    },

    clearEventDetails: () => {
        localStorage.removeItem(Constants.LOCAL_STORAGE.LOCATION_DETAILS);
        localStorage.removeItem(Constants.LOCAL_STORAGE.USERS_EVENT);
        localStorage.removeItem(Constants.LOCAL_STORAGE.EVENT);
    }
}

export default AuthenticationService;
